import { useCallback, useMemo, useEffect } from "react";
import { IconButton } from "../../../../../elements";
import RuleSelector from "./RuleSelector";
import TagsComponent from "./RuleTagsInput";
import {
  RuleEngineWrapper,
  RuleEngineRow,
  CloseButtonWrapper,
  ConditionWrapper,
  ButtonContainer,
} from "./RuleEngine.styled";
import RuleInput from "./RuleInput";
import { DateTimePicker, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TextField } from "@mui/material";
import dayjs from "dayjs";
import { useMediaQueries } from "../../../../../utils/mediaQueries";
import { useStore } from "../../../../../hooks";
import { observer } from "mobx-react";

import {
  RuleEngineEnums,
  OperatorsEnum,
  DefaultDestinyList,
} from "../../../../../utils/enums";
import {IconButtonWrapper} from "../../../../../elements/button/IconButton.styled";

const OPERATORS_FOR_NON_DATE_TIME_FIELDS = [
  RuleEngineEnums.Operators.IN,
  RuleEngineEnums.Operators.NOT_IN,
  RuleEngineEnums.Operators.BETWEEN,
];

const DATE_TIME_FIELD_TYPES = [
  RuleEngineEnums.FieldTypes.DATE,
  RuleEngineEnums.FieldTypes.TIME,
];

const DateTimePickerCustom = ({
  dataTestId,
  type = RuleEngineEnums.FieldTypes.DATE,
  ...props
}) => {
  const slots = { textField: TextField };
  const slotProps = {
    inputAdornment: { position: "start" },
    textField: { fullWidth: true, "data-testid": dataTestId },
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {type === RuleEngineEnums.FieldTypes.DATE ? (
        <DateTimePicker slots={slots} slotProps={slotProps} {...props} />
      ) : (
        <TimePicker slots={slots} slotProps={slotProps} {...props} />
      )}
    </LocalizationProvider>
  );
};

const RuleEngine = observer(
  ({
    ruleOperator,
    ruleGroupdID,
    index,
    onClose,
    fields,
    onChange,
    className = "",
    deleteIcon = "close",
    dataTestId,
    showClearButton = false,
    showConditionWrapper = false,
  }) => {
    const listDestine = useMemo(() => DefaultDestinyList, []);

    const { contactSegmentStore } = useStore();
    const { rulesStore } = contactSegmentStore;
    const rule = rulesStore.getRule(ruleGroupdID, index);

    const { isMobile, isTablet } = useMediaQueries;

    const formatDateTime = (timeObj, type) => {
      return type === RuleEngineEnums.FieldTypes.TIME
        ? timeObj.format("HH:mm")
        : timeObj.toISOString();
    };

    const formatReceiveDateTime = (timeStr, type) => {
      if (!timeStr) return dayjs();
      if (type !== RuleEngineEnums.FieldTypes.TIME) return dayjs(timeStr);
      const [hour, minute] = timeStr.split(":");
      return dayjs().hour(hour).minute(minute);
    };

    const origins = useMemo(
      () =>
        fields
          ? fields.map((field) => ({ name: field.name, value: field.origin }))
          : [],
      [fields],
    );

    const filteredFields = useMemo(() => {
      if (!rule.sourceData || !fields) return [];

      const selectedOrigin = fields.find(
        (field) => field.origin === rule.sourceData,
      );

      if (selectedOrigin) {
        return selectedOrigin.fields.map((field) => ({
          name: field.name,
          value: field.field,
          field_type: field.field_type,
          operators: field.operators,
          default_values: field.default_values,
        }));
      }

      return [];
    }, [rule.sourceData, fields]);

    const filteredFieldsVariable = useMemo(() => {
      const selectedOrigin = fields?.find(
        (field) => field.origin === rule.targetData,
      );
      if (selectedOrigin) {
        return selectedOrigin.fields.map((field) => ({
          name: field.name,
          value: field.field,
          field_type: field.field_type,
          operators: field.operators,
          default_values: field.default_values,
        }));
      }
      return [];
    }, [rule.targetData, fields]);

    const fieldType = useMemo(() => {
      if (filteredFields.length > 0 && rule.sourceField !== "") {
        const matchedField = filteredFields.find(
          (field) => field.value === rule.sourceField,
        );
        return matchedField ? matchedField.field_type : "";
      }
      return "";
    }, [rule.sourceField, filteredFields]);

    const filterOperators = useMemo(() => {
      if (filteredFields.length > 0 && rule.sourceField !== "") {
        const matchedField = filteredFields.find(
          (field) => field.value === rule.sourceField,
        );
        return matchedField && matchedField.operators
          ? matchedField.operators.map((op) => ({
              name: OperatorsEnum[op],
              value: op,
            }))
          : [];
      }
      return [];
    }, [filteredFields, rule.sourceField]);

    const onHandleClose = () => {
      const totalConditions = ruleOperator?.length || 0;
      if (totalConditions <= 1) {
        return;
      }
      onClose && onClose(index);
    };

    const onHandleOnChange = useCallback(() => {
      onChange &&
        onChange({
          index,
          data: {
            type: "CONDITION",
            source_data: rule.sourceData,
            source_field: rule.sourceField,
            field_type: rule.fieldType,
            operator: rule.operator,
            target_type: rule.targetType,
            target_data: rule.targetData,
            target_value: rule.targetValue,
          },
        });
    }, [
      index,
      rule.sourceData,
      rule.sourceField,
      fieldType,
      rule.operator,
      rule.targetType,
      rule.targetData,
      rule.targetValue,
      onChange,
    ]);

    const onHandleTargetValue = (value) => {
      rule.updateField("targetValue", value);
    };

    const onHandleVariableTarget = useCallback(
      (kind, value) => {
        if (kind === "data") rule.updateField("targetData", value);
        if (kind === "value") rule.updateField("targetValue", value);
      },
      [rule],
    );

    const onHandleRangeTargetValue = useCallback(
      (index, value) => {
        const currentValue = rule.targetValue;
        if (Array.isArray(rule.targetValue)) {
          currentValue[index] = value;
          rule.updateField("targetValue", [...currentValue]);
        }

        if (typeof rule.targetValue === "string") {
          const newValue = ["", ""];
          newValue[index] = value;
          rule.updateField("targetValue", newValue);
        }
      },
      [rule],
    );

    useEffect(() => {
      if (rule.targetValue && rule?.target_value !== rule.targetValue) {
        onHandleOnChange();
      }
    }, [rule.targetValue, rule?.target_value, onHandleOnChange, rule]);

    const isValidOperatorForFieldType =
      OPERATORS_FOR_NON_DATE_TIME_FIELDS.includes(rule.operator) &&
      !DATE_TIME_FIELD_TYPES.includes(fieldType);

    return (
      <RuleEngineWrapper className={className}>
        {showClearButton && (
          <CloseButtonWrapper>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onHandleClose();
              }}
            >
              Clear Filter
            </a>
          </CloseButtonWrapper>
        )}
        {showConditionWrapper && (
          <ConditionWrapper>
            <div>{ruleOperator}</div>
          </ConditionWrapper>
        )}

        <ButtonContainer id="remove-filter">
          <IconButtonWrapper>
            <IconButton
              icon="deleteOutline"
              width="20px"
              height="20px"
              onClick={() => onClose(index)}
            />
          </IconButtonWrapper>
        </ButtonContainer>

        <RuleEngineRow
          className={className}
          isMobile={isMobile}
          isTablet={isTablet}
        >
          <RuleSelector
            dataTestId={"dropdown-source-data-id"}
            suffix={"In"}
            options={origins}
            defaultValue={rule.sourceData}
            onSelect={(origin) => {
              if (rule.sourceData !== origin.value) {
                rule.updateField("sourceData", origin.value);
                onHandleOnChange();
              }
            }}
          />
          <RuleSelector
            dataTestId={"dropdown-source-field-id"}
            suffix={"If"}
            options={filteredFields}
            defaultValue={rule.sourceField}
            onSelect={(field) => {
              if (rule.sourceField !== field.value) {
                rule.updateField("sourceField", field.value);
                onHandleOnChange();
              }
            }}
          />
          <RuleSelector
            dataTestId={"dropdown-operator-id"}
            options={filterOperators}
            defaultValue={rule.operator}
            onSelect={(operator) => {
              if (rule.operator !== operator.value) {
                rule.updateField("operator", operator.value);
              }
            }}
          />

          <RuleSelector
            dataTestId={"dropdown-listDestine-id"}
            options={
              rule.operator === RuleEngineEnums.Operators.BETWEEN
                ? [
                    {
                      name: "Constant",
                      value: RuleEngineEnums.DestinyTypes.CONSTANT,
                    },
                  ]
                : listDestine
            }
            defaultValue={rule.targetType}
            onSelect={(destineValue) =>
              rule.updateField("targetType", destineValue.value)
            }
          />
        </RuleEngineRow>

        <RuleEngineRow className={className}>
          {rule.targetType === RuleEngineEnums.DestinyTypes.VARIABLE && (
            <>
              <RuleSelector
                dataTestId={"dropdown-variable-origin-id"}
                className={"dropdown-variable-origin"}
                suffix={"In"}
                options={origins}
                onSelect={(originValue) =>
                  onHandleVariableTarget("data", originValue.value)
                }
                defaultValue={rule.targetData}
              />
              <RuleSelector
                dataTestId={"dropdown-variable-field-id"}
                className={"dropdown-variable-field"}
                options={filteredFieldsVariable}
                onSelect={(value) =>
                  onHandleVariableTarget("value", value.value)
                }
                defaultValue={rule.targetValue}
              />
            </>
          )}
          {rule.targetType === RuleEngineEnums.DestinyTypes.CONSTANT &&
            !isValidOperatorForFieldType &&
            !DATE_TIME_FIELD_TYPES.includes(fieldType) && (
              <RuleInput
                dataTestId={"constant-field-value-id"}
                onChange={(inputValue) => onHandleTargetValue(inputValue)}
                defaultValue={rule.targetValue}
                type={fieldType}
              />
            )}
          {rule.targetType === RuleEngineEnums.DestinyTypes.CONSTANT &&
            OPERATORS_FOR_NON_DATE_TIME_FIELDS.slice(0, 2).includes(
              rule.operator,
            ) && (
              <TagsComponent
                dataTestId={"constant-tags-value-id"}
                onChange={(tags) => onHandleTargetValue(tags)}
                defaultValue={rule.targetValue}
              />
            )}
          {rule.targetType === RuleEngineEnums.DestinyTypes.CONSTANT &&
            rule.operator === RuleEngineEnums.Operators.BETWEEN &&
            !DATE_TIME_FIELD_TYPES.includes(fieldType) && (
              <>
                <RuleInput
                  dataTestId={"constant-field-value-start-id"}
                  type={fieldType}
                  defaultValue={
                    Array.isArray(rule.targetValue) && rule.targetValue.length
                      ? rule.targetValue[0]
                      : ""
                  }
                  onChange={(inputValue) =>
                    onHandleRangeTargetValue(0, inputValue)
                  }
                />
                <RuleInput
                  dataTestId={"constant-field-value-end-id"}
                  type={fieldType}
                  defaultValue={
                    Array.isArray(rule.targetValue) && rule.targetValue.length
                      ? rule.targetValue[1]
                      : ""
                  }
                  onChange={(inputValue) =>
                    onHandleRangeTargetValue(1, inputValue)
                  }
                />
              </>
            )}
          {rule.targetType === RuleEngineEnums.DestinyTypes.CONSTANT &&
            rule.operator !== RuleEngineEnums.Operators.BETWEEN &&
            DATE_TIME_FIELD_TYPES.includes(fieldType) && (
              <DateTimePickerCustom
                dataTestId={"constant-datetime-picker-id"}
                value={formatReceiveDateTime(rule.targetValue, fieldType)}
                onChange={(inputValue) =>
                  onHandleTargetValue(formatDateTime(inputValue, fieldType))
                }
                type={fieldType}
              />
            )}

          {rule.targetType === RuleEngineEnums.DestinyTypes.CONSTANT &&
            rule.operator === RuleEngineEnums.Operators.BETWEEN &&
            DATE_TIME_FIELD_TYPES.includes(fieldType) && (
              <>
                <DateTimePickerCustom
                  dataTestId={"constant-datetime-picker-start-id"}
                  value={formatReceiveDateTime(
                    rule.targetValue?.[0],
                    fieldType,
                  )}
                  onChange={(inputValue) =>
                    onHandleRangeTargetValue(
                      0,
                      formatDateTime(inputValue, fieldType),
                    )
                  }
                  type={fieldType}
                />
                <DateTimePickerCustom
                  dataTestId={"constant-datetime-picker-end-id"}
                  value={formatReceiveDateTime(
                    rule.targetValue?.[1],
                    fieldType,
                  )}
                  onChange={(inputValue) =>
                    onHandleRangeTargetValue(
                      1,
                      formatDateTime(inputValue, fieldType),
                    )
                  }
                  type={fieldType}
                />
              </>
            )}
        </RuleEngineRow>
      </RuleEngineWrapper>
    );
  },
);

export default RuleEngine;
